/**
 * Claim Due Date Utilities
 *
 * Calculates the adjusted claim due date for a project, handling:
 * - Day clamping (e.g., Feb 28 for claim_day=31)
 * - Weekend adjustment (Saturday/Sunday → next Monday)
 * - Status determination (green/orange/red)
 * - Forward-looking: when current month is claimed, shows next month's due
 */

export type ClaimDueStatus = "green" | "orange" | "red";

interface ClaimReport {
    claim_month: number;
    claim_year: number;
}

export interface ClaimDueInfo {
    status: ClaimDueStatus;
    /** The date being displayed (current or next month's due) */
    displayDate: Date;
    /** Whether the current month's claim has already been submitted */
    currentMonthClaimed: boolean;
    /** Human-readable label, e.g. "Mar 26" or "OVERDUE" */
    label: string;
}

/**
 * Get the claim date for a given month/year, adjusted for weekends.
 * If the day exceeds the month's last day, clamp to last day.
 * If it falls on Saturday → next Monday. Sunday → next Monday.
 */
export function getAdjustedClaimDate(
    claimDay: number,
    year: number,
    month: number // 1-indexed (1=Jan, 12=Dec)
): Date {
    // Clamp day to last day of month
    const lastDayOfMonth = new Date(year, month, 0).getDate();
    const day = Math.min(claimDay, lastDayOfMonth);

    const date = new Date(year, month - 1, day);
    const dayOfWeek = date.getDay();

    // Saturday (6) → Monday (+2), Sunday (0) → Monday (+1)
    if (dayOfWeek === 6) {
        date.setDate(date.getDate() + 2);
    } else if (dayOfWeek === 0) {
        date.setDate(date.getDate() + 1);
    }

    return date;
}

/**
 * Get the next upcoming claim date from today.
 * If this month's claim date hasn't passed, return this month's.
 * Otherwise return next month's.
 */
export function getNextClaimDate(claimDay: number, now?: Date): Date {
    const today = now || new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1; // 1-indexed

    const thisMonthDate = getAdjustedClaimDate(claimDay, year, month);

    if (thisMonthDate >= today) {
        return thisMonthDate;
    }

    // Move to next month
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    return getAdjustedClaimDate(claimDay, nextYear, nextMonth);
}

/**
 * Format the adjusted claim date for display (e.g., "Mar 26")
 */
export function formatClaimDate(date: Date): string {
    return date.toLocaleDateString("en-AU", { month: "short", day: "numeric" });
}

/**
 * Compute the full claim due info, the main entry point.
 *
 * Forward-looking logic:
 * - If current month is claimed → shows NEXT month's status and date
 * - If not claimed → shows current month with green/orange/red
 *
 * Status rules:
 * - GREEN: claimed or >3 days away
 * - ORANGE: ≤3 days to due and no claim yet
 * - RED: past due with no claim
 */
export function getClaimDueInfo(
    claimDay: number,
    reports: ClaimReport[],
    now?: Date
): ClaimDueInfo {
    const today = now || new Date();
    const currentMonth = today.getMonth() + 1; // 1-indexed
    const currentYear = today.getFullYear();

    // Check if current month's claim exists
    const hasCurrentMonthClaim = reports.some(
        (r) => r.claim_month === currentMonth && r.claim_year === currentYear
    );

    if (hasCurrentMonthClaim) {
        // Forward-looking: show NEXT month's due date with status
        const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
        const nextYear = currentMonth === 12 ? currentYear + 1 : currentYear;

        // Check if next month is also claimed (unlikely but possible)
        const hasNextMonthClaim = reports.some(
            (r) => r.claim_month === nextMonth && r.claim_year === nextYear
        );

        const nextDueDate = getAdjustedClaimDate(claimDay, nextYear, nextMonth);

        if (hasNextMonthClaim) {
            return {
                status: "green",
                displayDate: nextDueDate,
                currentMonthClaimed: true,
                label: `✓ ${formatClaimDate(nextDueDate)}`,
            };
        }

        // Compute status for next month's date relative to today
        const status = computeStatusForDate(nextDueDate, today);

        return {
            status,
            displayDate: nextDueDate,
            currentMonthClaimed: true,
            label:
                status === "red"
                    ? "OVERDUE"
                    : `${formatClaimDate(nextDueDate)}`,
        };
    }

    // Current month NOT claimed, standard logic
    const dueDate = getAdjustedClaimDate(claimDay, currentYear, currentMonth);
    const status = computeStatusForDate(dueDate, today);

    return {
        status,
        displayDate: dueDate,
        currentMonthClaimed: false,
        label: status === "red" ? "OVERDUE" : formatClaimDate(dueDate),
    };
}

/**
 * Compute green/orange/red for a specific due date.
 */
function computeStatusForDate(dueDate: Date, today: Date): ClaimDueStatus {
    const todayStart = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
    );
    const dueDateStart = new Date(
        dueDate.getFullYear(),
        dueDate.getMonth(),
        dueDate.getDate()
    );

    const diffMs = dueDateStart.getTime() - todayStart.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return "red";
    if (diffDays <= 3) return "orange";
    return "green";
}

/**
 * Legacy wrapper, used for sorting in useProjects
 */
export function getClaimDueStatus(
    claimDay: number,
    reports: ClaimReport[],
    now?: Date
): ClaimDueStatus {
    return getClaimDueInfo(claimDay, reports, now).status;
}

/**
 * Sorting priority: red=0, orange=1, green=2
 */
export function getStatusSortPriority(status: ClaimDueStatus): number {
    switch (status) {
        case "red":
            return 0;
        case "orange":
            return 1;
        case "green":
            return 2;
    }
}
